import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Paper,
  styled,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';
import Page from '../components/Page';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getImponible, singleImponible } from '../features/imponibles/imponiblesSlice';
import { store } from '../store';
import { getPlanesByResource, getPlanesList, planStatus } from '../features/planes/planesSlice';
import {
  ParsedCuota,
  getCuotasByResource,
  getCuotasList,
  getCuotasStatus
} from '../features/cuentacorriente/ctaCorrienteSlice';
import CtaCorriente from '../components/CtaCorriente';
import PlanesPago from '../components/PlanesPago';
import { getUrlLink } from '../features/ddjj/decJuradaSlice';
import automotorImg from '../assets/imponible-automotor.png';
import cementerioImg from '../assets/imponible-cementerio.png';
import comercioImg from '../assets/imponible-comercio.png';
import contribuyenteImg from '../assets/imponible-contribuyente.png';
import inmobiliarioImg from '../assets/imponible-inmobiliario.png';
import { CuotaPlan, PlanData } from '../features/planes/planesAPI';
import ConfirmModal from '../components/ConfirmModal';
import { ImponibleData, Recurso } from '../features/imponibles/imponiblesAPI';
import config from '../config';
import { createComprobante, getComprobante } from '../features/comprobantes/comprobantesSlice';
import { TabPanel } from '../components/TabPanel';
import { LoadingButton } from '@mui/lab';
import moment from 'moment';

const ContentWrapper = styled(Grid)(({ theme }) => ({
  padding: 0,
  paddingTop: '1.875rem',

  [theme.breakpoints.up('sm')]: {
    padding: '1.875rem',
  },
}));

const InfoWrapper = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.primary.light,
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
  height: '100%',
  alignItems: 'center',
  padding: '1.5rem 1.5rem',
  flexDirection: 'column',

  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
  },
}));

const ButtonWrapper = styled(Paper)(({ theme }) => ({
  border: '1px solid',
  borderColor: theme.palette.grey[300],

  [theme.breakpoints.up('sm')]: {
    padding: '1rem',
  },
}));

const ExpirationWrapper = styled(Paper)(({ theme }) => ({
  border: '1px solid',
  borderColor: theme.palette.grey[300],
  padding: '.5rem 1rem .5rem .75rem',

  [theme.breakpoints.up('sm')]: {
    padding: '1rem 2rem 1rem 1.5rem',
  },
}));

const ResourcesWrapper = styled(Paper)(({ theme }) => ({
  backgroundColor: 'rgba(245,248,250,1)',
  display: 'flex',
  width: '100%',
  height: '100%',
  padding: '.5rem',

  [theme.breakpoints.up('sm')]: {
    padding: '1rem',
  },
}));

const Resource = styled(Button)(() => ({
  lineHeight: '1.2',
  fontWeight: '700',
  marginRight: '1rem',
  padding: '.5rem 1rem',
  textTransform: 'uppercase',
  textAlign: 'left',
  width: '10rem',
  fontSize: '.8rem',
}));

const CustomButton = styled(Button)(({ theme }) => ({
  marginLeft: 10,
  padding: '.4rem 2rem',
  fontSize: '.75rem',
  fontWeight: '700',
  [theme.breakpoints.down('sm')]: {
    justifyContent: 'center',
    maxWidth: '6rem',
    margin: '0.4rem',
  },
}));

const CustomLoadingButton = styled(LoadingButton)(({ theme }) => ({
  marginLeft: 10,
  padding: '.4rem 2rem',
  fontSize: '.75rem',
  fontWeight: '700',
  [theme.breakpoints.down('sm')]: {
    justifyContent: 'center',
    maxWidth: '6rem',
    margin: '0.4rem',
  },
}));

const CustomBox = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'flex-end',
  flexWrap: 'nowrap',
  [theme.breakpoints.down('sm')]: {
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
}));

const UnlinkTaxButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.grey[300],
  color: theme.palette.grey[600],
  fontSize: '.75rem',
  fontWeight: 700,
  marginTop: '1rem',

  [theme.breakpoints.up('sm')]: {
    marginTop: 0,
  },
}));

const a11yProps = (index: number) => {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
};

const getImageImponible = (imponible: ImponibleData) => {
  switch (imponible.tipo) {
    case 'ROD': return automotorImg;
    case 'CEM': return cementerioImg;
    case 'INM': return inmobiliarioImg;
    case 'COM': return comercioImg;
    default: return contribuyenteImg;
  }
};

const getLabelImponible = (imponible: ImponibleData) => {
  switch (imponible.tipo) {
    case 'ROD': return `Dominio: ${imponible.informacion.dominio}`;
    case 'CEM': return `N° Nicho: ${imponible.informacion.nro_sepult}`;
    case 'INM': return `N° Partida: ${imponible.informacion.par_catastral}`;
    case 'CON': return `CUIT: ${imponible.informacion.cuim}`;
    default: return `CUIT: ${imponible.informacion.cuit}`;
  }
};

export default function MisImponibles() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { id: rawId } = useParams();

  const [isLoading, setLoading] = useState(false);

  if (!rawId) {
    navigate('/');
    return null;
  }

  const id = parseInt(rawId);

  // Controlador de boton Tabs
  const [value, setValue] = useState(0);

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const [openModal, setOpenModal] = useState(false);

  const planes = useSelector(getPlanesList);
  const plansStatus = useSelector(planStatus);

  const cuotas = useSelector(getCuotasList);
  const cuotasStatus = useSelector(getCuotasStatus);

  const url = useSelector(getUrlLink);
  const imponible = useSelector(singleImponible);

  const [selectedResource, setSelectedResource] = useState<null | Recurso>(null);
  const [selectedCuotasCC, setSelectedCuotasCC] = useState<ParsedCuota[]>([]);
  const [selectedCuotasPP, setSelectedCuotasPP] = useState<CuotaPlan[]>([]);
  const [canCreatePlan, setCanCreatePlan] = useState(false);
  const [periodCuotas, setPeriodCuotas] = useState('');


  const [textVisible, setTextVisible] = useState(true);

  const [total, setTotal] = useState(0);
  const [juicio, setJuicio] = useState(false);

  useEffect(() => {
    if (imponible?.id == id) {
      setSelectedResource(imponible.recursos[0]);

      if (imponible.apremio) {
        setJuicio(true);
      } else {
        store.dispatch(getCuotasByResource({
          tipo: imponible.tipo,
          id: imponible.imponible,
          recurso: imponible.recursos[0].recurso,
        }));
      }

      store.dispatch(getPlanesByResource({
        tipo: imponible.tipo,
        id: imponible.imponible,
        recurso: imponible.recursos[0].recurso,
      }));

      if (imponible.tipo == 'CON' && imponible.recursos[0].recurso == '14') {
        setTextVisible(false)
      };

      return;
    }

    store.dispatch(getImponible(id))
      .unwrap()
      .then(imponible => {
        const data: PlanData = {
          tipo: imponible.tipo,
          id: imponible.imponible,
          recurso: imponible.recursos[0].recurso,
        };

        setSelectedResource(imponible.recursos[0]);

        if (imponible.apremio) {
          setJuicio(true);
        } else {
          store.dispatch(getCuotasByResource(data));
        }

        store.dispatch(getPlanesByResource(data));
      });
  }, []);

  useEffect(() => {
    // Controla que las ninguna de las cuotas seleccionadas supere la fecha de hoy. Si la supera bloquea el boton de crear plan
    //CAMBIO => solo se activa el boton de plan de pagos si es distinto al mes actual
    const now = new Date();

    let newCanCreatePlan = selectedCuotasCC && selectedCuotasCC.length > 0 && !selectedCuotasCC.some(cuota => {
      const fechaVto = new Date(cuota.fecha_vto.split('/').reverse().join('-'));
      ////return fechaVto.getTime() >= now.getTime() || moment(fechaVto).isSame(now, 'month');
      return moment(fechaVto).isSame(now, 'month');
    });

    newCanCreatePlan = newCanCreatePlan && selectedResource?.planes != '0';

    setCanCreatePlan(newCanCreatePlan);

    // String de Cuotas seleccionadas para Imprimir
    const newPeriodCuotas = selectedCuotasCC
      .map(cuota => ('0' + cuota.cuota).slice(-2) + '/' + cuota.anio)
      .join(',');

    setPeriodCuotas(newPeriodCuotas);
  }, [selectedCuotasCC]);

  useEffect(() => {
    // No se puede crear planes para las cuotas de los planes
    setCanCreatePlan(false);

    // String de Cuotas seleccionadas para Imprimir
    const newPeriodCuotas = selectedCuotasPP
      .map(cuota => cuota.cuota + '/' + cuota.nro_comprob.substring(0, cuota.nro_comprob.length - 3))
      .join(',');

    setPeriodCuotas(newPeriodCuotas);
  }, [selectedCuotasPP]);

  useEffect(() => {
    // Cada vez que se cambie de recurso o entre cuenta corriente y plan de pago, reseteamos los datos
    setTotal(0);
    //Se saco para que no refresque y pierda el valor de mponible.apremio == true
    /////setJuicio(false);
    setSelectedCuotasCC([]);
    setSelectedCuotasPP([]);
  }, [value, selectedResource]);

  const handlePayment = () => {
    if (!imponible || !selectedResource) {
      return;
    }

    setLoading(true);

    store.dispatch(createComprobante({
      tipo: imponible.tipo,
      imponible: imponible.imponible,
      recurso: selectedResource.recurso,
      periods: selectedCuotasCC.length > 0
        ? selectedCuotasCC.map(c => c.cuota.padStart(2, '0') + '/' + c.anio)
        : selectedCuotasPP.map(c => c.cuota + '/' + c.nro_comprob.substring(0, c.nro_comprob.length - 3)),
      plan: selectedCuotasPP.length > 0 ? 1 : undefined,
    })).unwrap().then(() => {
      navigate('/pagar');
    });
  };

  const handleAnualPayment = () => {
    if (!imponible || !selectedResource || !selectedResource.anual) {
      return;
    }

    setLoading(true);

    store.dispatch(getComprobante({
      tipo: 18,
      numero: parseInt(selectedResource.anual),
    })).unwrap()
      .then(() => {
        navigate('/pagar');
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const handlePlan = () => {
    if (!imponible || !selectedResource) {
      return;
    }

    navigate('/planes', {
      state: {
        selectedCuotas: selectedCuotasCC,
        resourceNumber: selectedResource.recurso,
        imponible,
      },
    });
  };

  const handlePrint = () => {
    if (!imponible || !selectedResource) {
      return;
    }

    let imprimirUrl = `${config.api.url}/pdf/boleta/${imponible?.tipo}-${imponible?.imponible}/${selectedResource?.recurso}?periodos=${periodCuotas}`;

    if (selectedCuotasPP.length > 0) {
      imprimirUrl += '&plan=1';
    }

    window.open(imprimirUrl);
  };

  return (
    <Page>
      <Container>
        <ContentWrapper container>
          <Grid item xs={12} mb={2}>
            <InfoWrapper>
              {
                !imponible
                  ? <CircularProgress size={20} />
                  : <>
                    <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                      <img
                        src={getImageImponible(imponible)}
                        style={{ alignSelf: 'center', height: '40px' }}
                      />
                      <Box ml={2}>
                        <Typography
                          variant="body1"
                          sx={{
                            fontSize: '1rem',
                            fontWeight: 700,
                            color: '#36B7E8',
                          }}
                        >
                          {imponible.referencia}
                        </Typography>
                        <Typography variant="body1">Destinatario: {imponible.informacion.resp_pago}</Typography>
                        <Typography variant="body1">{getLabelImponible(imponible)}</Typography>
                        {imponible.debito ? <Typography variant="body1" sx={{
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          color: '#36B7E8',
                        }}>Este imponible está adherido al Débito Automático</Typography> : <Typography variant="body1" sx={{
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          color: '#e83636',
                        }}>Sin adhesión al Débito Automático.</Typography>}
                      </Box>
                    </Box>
                  </>
              }
              <UnlinkTaxButton
                variant="contained"
                onClick={() => setOpenModal(true)}
              >
                DESVINCULAR IMPONIBLE
              </UnlinkTaxButton>
            </InfoWrapper>
            {
              imponible
                ? <ConfirmModal
                  openModal={openModal}
                  setOpenModal={setOpenModal}
                  title="Quitar Imponible"
                  text="¿Seguro que desea desvincular el elemento de su consulta?"
                  imponible={imponible}
                />
                : null
            }
          </Grid>
          <Grid item xs={12}>
            <ButtonWrapper>
              <ResourcesWrapper>
                {
                  imponible?.recursos.map((resource, i) => (
                    <Resource
                      onClick={() => {
                        setSelectedResource(resource);
                        const data = {
                          tipo: imponible.tipo,
                          id: imponible.imponible,
                          recurso: resource.recurso,
                        };
                        store.dispatch(getPlanesByResource(data));
                        store.dispatch(getCuotasByResource(data));
                      }}
                      key={i}
                      sx={{
                        backgroundColor: selectedResource && selectedResource.recurso === resource.recurso
                          ? theme.palette.primary.main
                          : 'transparent',
                        color: selectedResource && selectedResource.recurso === resource.recurso
                          ? 'white'
                          : theme.palette.grey[700],
                      }}
                    >
                      {resource.descripcion}
                    </Resource>
                  ))}
              </ResourcesWrapper>
            </ButtonWrapper>
          </Grid>
          <Grid item xs={12} mt={2}>
            <ExpirationWrapper>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs
                    value={value}
                    onChange={handleChange}
                    aria-label="basic tabs example"
                  >
                    <Tab label="Cuenta Corriente" {...a11yProps(0)} />
                    <Tab label="Plan de Pago" {...a11yProps(1)} />
                  </Tabs>
                </Box>
                {imponible?.tipo === 'COM' ? (
                  <Button
                    onClick={() => window.open(url)}
                    sx={{
                      [theme.breakpoints.down('sm')]: {
                        display: 'none',
                      },
                    }}
                  >
                    Declaraciones Juradas
                  </Button>
                ) : null}
              </Box>

              {
                juicio
                  ? <TabPanel value={value} index={0}>
                    <Alert severity="error">
                      UD.TIENE PERÍODOS EN JUICIO – DEBERÁ DIRIGIRSE A LA DIRECCIÓN DE ASUNTOS LEGALES
                      SITO EN LA CALLE  BELGRANO 1334 DE LA LOCALIDAD DE SAN MIGUEL
                      EN EL HORARIO DE 8.00 A 14.00 HS.
                    </Alert>
                  </TabPanel>
                  : <CtaCorriente
                    value={value}
                    cuotas={cuotas}
                    selectedCuotas={selectedCuotasCC}
                    setSelectedCuotas={setSelectedCuotasCC}
                    total={total}
                    setTotal={setTotal}
                    status={cuotasStatus}
                    textVisible={textVisible}
                  />
              }
              <PlanesPago
                value={value}
                planes={planes}
                status={plansStatus}
                selectedCuotas={selectedCuotasPP}
                setSelectedCuotas={setSelectedCuotasPP}
              />

              <CustomBox>
                <CustomButton
                  variant="outlined"
                  disabled={isLoading || periodCuotas === '' ? true : false}
                  onClick={handlePrint}
                >
                  Imprimir
                </CustomButton>
                <CustomButton
                  variant="outlined"
                  disabled={isLoading || !selectedResource?.anual ? true : false}
                  onClick={() => {
                    const comprobanteURL = `${config.api.url}/pdf/comprobante/18-${selectedResource?.anual}`;
                    window.open(comprobanteURL);
                  }}
                >
                  Comprobante {selectedResource?.anual && selectedResource?.semestral ? 'Semestral' : 'Anual'}
                </CustomButton>

                <CustomButton
                  variant="outlined"
                  disabled={isLoading || !selectedResource?.anual ? true : false}
                  onClick={handleAnualPayment}
                >
                  Pago {selectedResource?.anual && selectedResource?.semestral ? 'Semestral' : 'Anual'}
                </CustomButton>

                <CustomButton
                  variant="outlined"
                  disabled={isLoading || !canCreatePlan}
                  onClick={handlePlan}
                >
                  + CREAR PLAN
                </CustomButton>

                <CustomLoadingButton
                  variant="contained"
                  disabled={
                    (!selectedCuotasCC || !selectedCuotasCC.length) && (!selectedCuotasPP || !selectedCuotasPP.length)
                  }
                  onClick={handlePayment}
                  sx={{ color: 'white' }}
                  loading={isLoading}
                >
                  PAGAR AHORA
                </CustomLoadingButton>
              </CustomBox>
            </ExpirationWrapper>
          </Grid>
        </ContentWrapper>
      </Container>
    </Page>
  );
}
